import React, { useEffect, useState, useRef, useMemo } from "react";
import useSWR from "swr";
import { useRouter } from 'next/router';
import url from "url";
import ItemFocus from './ItemFocus';
import { ChevronRight } from "lucide-react";

import {
  Alert,
  Button,
  ListGroup,
  ListGroupItem,
  Row,
  Col,
  Badge
} from "react-bootstrap"

import Select from "../Controls/CustomSelect"
import dataf from "../../data/rating.json"
import Pagination from "../Pagination"
import Image from "../CustomImage"
import fetcher from "../../lib/fetcher"
import constants from "../../lib/constants"

//--
const parser = (data) => {
  return data
    .map((item) => (
      {
        company_id: item.company_id,
        points: item.points,
        percent: item.percent,
        value: item.value,
        label: item.label,
        title: item.title,
        website_url: item.website_url ? item.website_url : '/',
        vendor_url: item.vendor_url ? `/company/${item.vendor_url}` : '#',
        logo_url: item.logo_url ? `${constants.LOGO_URL}/${item.logo_url}` : '#',
        founded: item.founded || '',
        location: `${item.location_city || ''}, ${item.location_country?.value || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || 'N/A',
        company_size: (item.company_size || '').replace('employees', ''),
        hourly_rate: item.hourly_rate,
        minimal_project: item.project_minimal,
        ready_to_start: item.ready_to_start,
        highligh1: item.highligh1,
        highligh2: item.highligh2,
        highligh3: item.highligh3,
        clients: (item.clients.length >= 12) ? item.clients.slice(0, 12) : item.clients || [],
        description: item.description,
        row_count: item.row_count,
        is_sponsor: item.is_sponsor,
        focus_name: item.focus_name,
        focus: item.focus,
        sort_id: item.sort_id,
        score: item.score || []
      }
    ));
}

//--
const queryString = (params) => Object.keys(params).map(key => {
  const value = params[key];
  if (Array.isArray(value)) {
    return value.map(v => `${key}=${v.value}`).join('&');
  }
  if (value && typeof value.value !== 'undefined') {
    return `${key}=${value.value}`;
  }
  return '';
}).filter(Boolean).join('&');

const queryString2 = (params) => Object.keys(params).map(key => {
  const value = params[key];
  if (Array.isArray(value)) {
    return value.map(v => `${key}=${v}`).join('&');
  }
  return `${key}=${value}`;
}).join('&');

//--
const isFormInputs = (obj) => (Object.keys(obj).length > 0);

//--
const Page = ({ data, linkage, isAdmin }) => {

  const toMatrix = (arr, width) =>
    arr.reduce((rows, key, index) => (index % width == 0 ? rows.push([key])
      : rows[rows.length - 1].push(key)) && rows, []);

  const getTruncatedText = (text, maxLength = 100) => {
    return text?.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  }

  const getRandomRating = (item) => item.score?.find(x => x.code === 'MRTR')?.points || 0;


  return (
    <ListGroup className="mb-5 rounded-0">
      {data.length === 0 && (
        // <ListGroupItem action className="p4" key={0}>
        (<Row>
          <Col>
            <Alert variant="info" className="mb-0" >
              Sorry, there are no companies meeting your selected parameters at the moment.
              We keep looking for and adding new prominent service providers on the market,
              so please try again later or search using broader parameters to explore more.
            </Alert>
          </Col>
        </Row>)
        // </ListGroupItem>
      )}
      {data.map((item, index) => (
        // <Link href={item.vendor_url} passHref key={index}>
        (<ListGroupItem action className="p4" key={index}>
          <Row>
            <Col lg="4" className="align-self-start mb-4 mb-lg-0">
              <div className="d-flex mb-3 example-parent justify-content-between align-items-center flex-wrap">
                <div className="d-flex align-items-center mb-2 me-auto">
                  <h3 className="h5 mb-0">{item.title}</h3>
                  <div className="avatar avatar-sm avatar-border-white ms-2 overflow-hidden" style={{ flexShrink: 0 }}>
                    <Image
                      src={item.logo_url}
                      alt={`${item.title} ${linkage?.label || ''} company logo`}
                      width={36}
                      height={36}
                      className="d-block"
                    />
                  </div>
                </div>

                {/* Compact Reviews Badge */}
                {parseFloat(item.score?.find(x => x && x.code === 'MREV')?.points || 0) > 0 && (() => {
                  const isLocal = item.score?.some(x => x && x.is_local_reviews);
                  return (
                    <div className="d-flex align-items-center mb-2 ms-auto">
                      <div className="d-flex flex-column text-end me-2" style={{ lineHeight: "1.1" }}>
                        <span className="fw-bold" style={{ fontSize: "12px", color: "#212529" }}>
                          {isLocal ? 'Vendorland Reviews' : 'External Reviews'}
                        </span>
                        <span style={{ fontSize: "12px", color: "#6c757d" }}>
                          {item.score?.find(x => x && x.reviews_rating_source !== undefined)?.reviews_rating_source || ''}
                        </span>
                      </div>
                      
                      {/* Reviews shaded badge */}
                      <div 
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: "34px",
                          height: "34px",
                          background: isLocal ? "#43128e" : "#F0F0F0",
                          borderRadius: "6px 6px 6px 0px",
                          flexShrink: 0
                        }}
                      >
                        <span 
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: "600",
                            fontSize: "14px",
                            color: isLocal ? "#FFFFFF" : "#222529"
                          }}
                        >
                          {parseFloat(item.score?.find(x => x && x.code === 'MREV')?.points || 0).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {isAdmin &&
                  <div className="w-100 d-flex mt-1">
                    <Badge bg="dark-light" text="dark" className="p-1 mb-1 me-1" style={{ fontSize: "10px" }}>
                      {item.points || 0}
                    </Badge>
                    {item.sort_id && (
                      <Badge bg="info" className="p-1 mb-1 me-1" style={{ fontSize: "10px" }}>
                        {item.sort_id}
                      </Badge>
                    )}
                    <Badge bg="success" className="p-1 mb-1" style={{ fontSize: "10px" }}>
                      {getRandomRating(item)}
                    </Badge>
                  </div>
                }
              </div>
              <p className="text-sm">{getTruncatedText(item.description)}</p>
              <Button
                href={item.vendor_url}
                variant="link"
                className="ps-0 fw-bold d-inline-flex align-items-center"
              >
                VIEW PROFILE <ChevronRight className="ms-2" size={12} />
              </Button>
            </Col>
            <Col lg="8">
              <Row>
                <Col xs="6" md="4" lg="3" className="py-3 mb-3 mb-lg-0">
                  <div className="label-heading">Company size</div>
                  <p className="text-sm fw-bold">
                    {item.company_size}
                  </p>
                  <div className="label-heading">Founded</div>
                  <p className="text-sm fw-bold mb-0">
                    {item.founded}
                  </p>
                </Col>
                <Col xs="6" md="4" lg="3" className="py-3 mb-3 mb-lg-0">
                  <div className="label-heading">Hourly rate</div>
                  <p className="text-sm fw-bold">
                    {item.hourly_rate}
                  </p>
                  <div className="label-heading">Minimal project</div>
                  <p className="text-sm fw-bold mb-0">
                    {item.minimal_project}
                  </p>
                </Col>
                <Col xs="6" md="4" lg="3" className="py-3 mb-3 mb-lg-0">
                  <div className="label-heading">Headquarters</div>
                  <p className="text-sm fw-bold">
                    {item.location}
                  </p>
                  <div className="label-heading">Ready to start</div>
                  <p className="text-sm fw-bold mb-0">
                    {item.ready_to_start}
                  </p>
                </Col>
                <Col xs="6" md="4" lg="3" className="py-3 mb-3 mb-lg-0 text-lg-end"> {/*  className="align-self-center" */}
                  {item.is_sponsor &&
                    <div className="label-heading">Sponsor</div>
                  }
                  <Button
                    href={item.website_url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    variant="secondary"
                  >
                    Visit website
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col md="12" lg="12" className="py-3 mb-3 mb-lg-0">
                  <ItemFocus item={item} linkage={linkage} />
                </Col>
              </Row>
            </Col>
          </Row>
        </ListGroupItem>)
        // </Link>
      ))}
    </ListGroup>
  );

}

//--
function useData(linkage, formInputs, page, initialData) {
  let cURL = `${constants.BASE_URL}/companies/${linkage.parent_value}?page=${page}`;
  
  if (linkage.focus && linkage.value) {
    cURL += `&${linkage.focus}=${linkage.value}`;
  }
  
  if (linkage.country) cURL += `&country=${linkage.country}`;
  if (linkage.state) cURL += `&state=${linkage.state}`;
  if (linkage.city) cURL += `&city=${linkage.city}`;
  
  cURL = linkage.item && linkage.item.params ? `${cURL}&${queryString2(linkage.item.params)}` : cURL;
  cURL = isFormInputs(formInputs) ? `${cURL}&${queryString(formInputs)}` : cURL;
  
  const { data, error, isLoading } = useSWR(cURL, fetcher, { fallbackData: initialData, revalidateOnMount: false });
  
  const companies = useMemo(() => data ? parser(data) : [], [data]);

  return {
    companies,
    isLoading,
    isError: error
  }
}

//--
const CompanyList = ({ props }) => {
    const { catalog, linkage, user, headlines, dataTable, initialFilters } = props;
  const [formInputs, setFormInputs] = useState(initialFilters || {});
  const [initParams, setInitParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isUserAction, setIsUserAction] = useState(false);
  const isUserActionRef = useRef(false);
  const isFirstRender = useRef(true);
  const { industry, hourly_rate = [], company_size = [], location = [], project_target_size = [], init_params = {} } = catalog;
  const project_size = project_target_size;
  const router = useRouter();
  const { query, asPath } = router;

  useEffect(() => {
    let obj = {};

    if (init_params.industry) {
      const value = industry.find((f) => f.value === init_params.industry);
      value && (obj = ({ ...obj, ['industry']: [value] }))
    }

    if (init_params.country) {
      const value = location.find((f) => f.value === init_params.country);
      value && (obj = ({ ...obj, ['country']: [value] }))
    }

    if (Object.keys(obj).length > 0) {
      setInitParams(obj);
    }

  }, [init_params]);

  useEffect(() => {
    if (isUserActionRef.current) {
      isUserActionRef.current = false;
      return;
    }
    
    let obj = {};

    if (query.project_size) {
      const projectSizeValues = Array.isArray(query.project_size) ? query.project_size : [query.project_size];
      const values = project_size.filter(f => projectSizeValues.includes(f.value));
      if (values.length > 0) {
        obj = { ...obj, ['project_size']: values };
      }
    }

    if (query.industry) {
      const industryValues = Array.isArray(query.industry) ? query.industry : [query.industry];
      const values = industry.filter(f => industryValues.includes(f.value));
      if (values.length > 0) {
        obj = { ...obj, ['industry']: values };
      }
    }

    if (query.hourly_rate) {
      const hourlyRateValues = Array.isArray(query.hourly_rate) ? query.hourly_rate : [query.hourly_rate];
      const values = hourly_rate.filter(f => hourlyRateValues.includes(f.value));
      if (values.length > 0) {
        obj = { ...obj, ['hourly_rate']: values };
      }
    }

    if (query.company_size) {
      const companySizeValues = Array.isArray(query.company_size) ? query.company_size : [query.company_size];
      const values = company_size.filter(f => companySizeValues.includes(f.value));
      if (values.length > 0) {
        obj = { ...obj, ['company_size']: values };
      }
    }

    if (query.country) {
      const countryValues = Array.isArray(query.country) ? query.country : [query.country];
      const values = location.filter(f => countryValues.includes(f.value));
      if (values.length > 0) {
        obj = { ...obj, ['country']: values };
      }
    }

    if (query.sortby) {
      const value = dataf.sortby.find((f) => f.value === query.sortby);
      value && (obj = ({ ...obj, ['sortby']: value }))
    }

    if (query.page) {
      const page = Number(query.page);
      //(page > 1) && (obj = ({ ...obj, ['page']: { id: page, value: page } })) &&
      (page > 1) && setCurrentPage(page)
    }

    if (Object.keys(obj).length > 0) {
      setFormInputs(obj);
    }

  }, [query]);

  const getURL = () => `${url.parse(asPath).pathname}${isFormInputs(formInputs) ? '?' + queryString(formInputs) : ''}`
  const { companies, isLoading, isError } = useData(linkage, formInputs, currentPage, dataTable)

  // TODO :console.log(companies)

  /*   const updateCountry = async (country) => {
      const cURL = getURL();
      const urlCountryCode = (query.slug[1] && query.slug[1].length == 2) ? query.slug[1] : '';
      let newUrl = cURL;
  
      if (urlCountryCode && country) {
        newUrl = cURL.replace(`/${urlCountryCode}`, `/${country}`);
      }
  
      if (!urlCountryCode && country) {
        newUrl = cURL.replace(`/${query.slug[0]}`, `/${query.slug[0]}/${country}`);
      }
  
      router.push(newUrl, undefined, { shallow: false })
    } */

  useEffect(() => {
    let cURL = getURL();

    if (isFormInputs(formInputs)) {
      cURL = (currentPage > 1) ? `${cURL}&page=${currentPage}` : cURL;
    } else {
      cURL = (currentPage > 1) ? `${cURL}?page=${currentPage}` : cURL;
    }

    router.push(cURL, undefined, { shallow: true })

  }, [formInputs, currentPage])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const onSelectChange = (name, e) => {
    isUserActionRef.current = true;
    setFormInputs(prev => ({ ...prev, [name]: e }))
    setCurrentPage(1)
  }

  const onClearAll = async (e) => {
    setIsUserAction(true);
    //const queryExt = JSON.parse(JSON.stringify(query))
    //const cURL = url.parse(asPath).pathname;
    setFormInputs({});
    //const newUrl = country ? cURL.replace(`/${query.slug[0]}/${query.slug[1]}`, `/${query.slug[0]}`) : cURL;
    //router.push(`${newUrl}`, undefined, { shallow: !country });
    setCurrentPage(1);
  }

  if (isError) return <h3>{isError?.message || "An error occurred while loading companies."}</h3>

  return (
    <React.Fragment>
      {/*
      <div>
        <h3 className="my-3">{dataf.subtitle}</h3>
      </div>
       <Nav tabs className="text-xl fw-bold">
        {project_size.map((tab, index) => (
          <NavItem
            key={index}
          //className={index < tabs.length - 1 ? "mr-1" : ""}
          >
            <NavLink
              onClick={() => toggle(index)}
              className={activeTab === index ? "active" : ""}
            >
              {tab.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
*/}
      <div>
          <div className="d-flex flex-wrap align-items-center mb-4 mt-3">
          {!Boolean(initParams["project_size"]) &&
          <Select           
            id="project_size"
            instanceId="project_size"
            isMulti
            isSearchable
            options={project_size}
            placeholder="Budget range"
            label="Budget"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0 dropdown bootstrap-select z-index-20 budget-select"
            classNamePrefix="selectpicker"
            isDisabled={Boolean(initParams["project_size"])}
            value={initParams["project_size"] || formInputs["project_size"] || null}
            onChange={(e) => onSelectChange("project_size", e)}
          />
          }
          {!Boolean(initParams["industry"] || linkage.focus === "industry") &&
          <Select
            id="industry"
            instanceId="industry"
            isMulti
            isSearchable
            options={industry}
            placeholder="Industry focus"
            label="Industry"
            pluralLabel="Industries"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0 dropdown bootstrap-select z-index-20 industry-select"
            classNamePrefix="selectpicker"
            isDisabled={Boolean(initParams["industry"] || linkage.focus === "industry")}
            value={initParams["industry"] || formInputs["industry"] || (linkage.focus === "industry" ? industry.filter(i => i.value === linkage.value) : null)}
            onChange={(e) => onSelectChange("industry", e)}
          />
          }
          {!Boolean(initParams["country"] || linkage.focus === "location") &&
          <Select
            id="location"
            instanceId="location"
            isMulti
            options={location}
            placeholder="Country"
            label="Country"
            pluralLabel="Countries"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0 dropdown bootstrap-select z-index-20 country-select"
            classNamePrefix="selectpicker"
            isDisabled={Boolean(initParams["country"] || linkage.focus === "location")}
            value={initParams["country"] || formInputs["country"] || (linkage.focus === "location" ? location.filter(l => l.value === linkage.value) : null)}
            onChange={(e) => onSelectChange("country", e)}
          />
          }
          <Select
            id="hourly_rate"
            instanceId="hourly_rate"
            isMulti
            options={hourly_rate}
            placeholder="Hourly rates"
            label="Rate"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0 dropdown bootstrap-select z-index-20 rate-select"
            classNamePrefix="selectpicker"
            value={formInputs["hourly_rate"] || null}
            onChange={(e) => onSelectChange("hourly_rate", e)}
          />
          <Select
            id="company_size"
            instanceId="company_size"
            isMulti
            options={company_size}
            placeholder="Company size"
            label="Size"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0 dropdown bootstrap-select z-index-20 size-select"
            classNamePrefix="selectpicker"
            value={formInputs["company_size"] || null}
            onChange={(e) => onSelectChange("company_size", e)}
          />
          <Button
            variant="outline-primary"
            className="ms-auto ms-md-0 me-3 mb-3 mb-lg-0"
            onClick={(e) => onClearAll(e)}
          >Clear all
          </Button>
          <div className="d-flex align-items-center mb-3 mb-lg-0 ms-auto">
            <label className="form-label me-2 mb-0 text-nowrap">Sort by</label>
            <Select
              id="reactselect"
              instanceId="reactselect"
              options={dataf.sortby}
              defaultValue={dataf.sortby[0]}
              className="dropdown bootstrap-select sort-select"
              classNamePrefix="selectpicker"
              value={formInputs["sortby"] || dataf.sortby[0]}
              onChange={(e) => onSelectChange("sortby", e)}
            />
          </div>

        </div>
        <div className="h6 text-muted mb-4 mt-3">{headlines.tableTitle}</div>

      </div>
      {isLoading ? (
        <div className="text-center py-4 mb-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <Page key={0} data={companies} linkage={linkage} isAdmin={user && user.is_admin} />
      )}

      {(!isLoading && companies && companies.length > 0) && (
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowCount={companies[0].row_count || 0}
          pageLimit={5}
          dataLimit={50}
        />
      )}
    </React.Fragment>
  )
}

export default CompanyList